import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import {
  TrendingUp,
  Users,
  Target,
  Calendar,
  Network,
  Award,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Download,
  Search,
  X,
  BarChart3,
  Activity,
  Zap,
  UserCheck,
  Phone,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import Navbar from "../../components/Navbar";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const PublisherConversionAnalytics = () => {
  const [timeRange, setTimeRange] = useState("30days");
  const [searchTerm, setSearchTerm] = useState("");
  const [networkFilter, setNetworkFilter] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // Mock data for conversions
  const mockConversionData = useMemo(
    () => ({
      summary: {
        totalPotentialPublishers: 1247,
        totalConverted: 389,
        conversionRate: 31.2,
        avgConversionTime: 4.3,
        thisMonthConversions: 87,
        lastMonthConversions: 72,
        growthRate: 20.8,
      },
      networkBreakdown: {
        MTN: { total: 523, converted: 178, rate: 34.0 },
        VDF: { total: 412, converted: 121, rate: 29.4 },
        TGO: { total: 312, converted: 90, rate: 28.8 },
      },
      conversionTrend: [
        { date: "2026-01-01", conversions: 12, potentials: 45 },
        { date: "2026-01-02", conversions: 15, potentials: 52 },
        { date: "2026-01-03", conversions: 18, potentials: 48 },
        { date: "2026-01-04", conversions: 14, potentials: 41 },
        { date: "2026-01-05", conversions: 20, potentials: 55 },
        { date: "2026-01-06", conversions: 22, potentials: 58 },
        { date: "2026-01-07", conversions: 19, potentials: 50 },
        { date: "2026-01-08", conversions: 25, potentials: 62 },
        { date: "2026-01-09", conversions: 23, potentials: 59 },
        { date: "2026-01-10", conversions: 28, potentials: 68 },
        { date: "2026-01-11", conversions: 26, potentials: 64 },
        { date: "2026-01-12", conversions: 24, potentials: 61 },
        { date: "2026-01-13", conversions: 30, potentials: 72 },
        { date: "2026-01-14", conversions: 27, potentials: 67 },
        { date: "2026-01-15", conversions: 31, potentials: 75 },
        { date: "2026-01-16", conversions: 29, potentials: 70 },
        { date: "2026-01-17", conversions: 32, potentials: 78 },
        { date: "2026-01-18", conversions: 35, potentials: 82 },
        { date: "2026-01-19", conversions: 33, potentials: 80 },
        { date: "2026-01-20", conversions: 38, potentials: 88 },
      ],
      topReferrers: [
        { code: "INF001", name: "Sarah Johnson", conversions: 45, rate: 68.2 },
        { code: "INF002", name: "Michael Chen", conversions: 38, rate: 61.3 },
        { code: "INF003", name: "Emma Wilson", conversions: 32, rate: 58.2 },
        { code: "INF004", name: "James Brown", conversions: 28, rate: 54.9 },
        { code: "INF005", name: "Lisa Anderson", conversions: 24, rate: 52.2 },
      ],
      conversionsByDayOfWeek: {
        Monday: 68,
        Tuesday: 72,
        Wednesday: 65,
        Thursday: 58,
        Friday: 48,
        Saturday: 38,
        Sunday: 40,
      },
      conversionVelocity: [
        { range: "0-1 days", count: 142, percentage: 36.5 },
        { range: "2-3 days", count: 98, percentage: 25.2 },
        { range: "4-7 days", count: 87, percentage: 22.4 },
        { range: "8-14 days", count: 42, percentage: 10.8 },
        { range: "15+ days", count: 20, percentage: 5.1 },
      ],
      detailedConversions: [
        {
          id: 1,
          publisherName: "Alice Thompson",
          phone: "0244123456",
          network: "MTN",
          registeredDate: "2026-01-15",
          convertedDate: "2026-01-18",
          daysToConvert: 3,
          referredBy: "INF001",
          affiliateId: "AFF1234",
        },
        {
          id: 2,
          publisherName: "Bob Martinez",
          phone: "0509876543",
          network: "VDF",
          registeredDate: "2026-01-14",
          convertedDate: "2026-01-19",
          daysToConvert: 5,
          referredBy: "INF002",
          affiliateId: "AFF1235",
        },
        {
          id: 3,
          publisherName: "Carol Davis",
          phone: "0277654321",
          network: "TGO",
          registeredDate: "2026-01-16",
          convertedDate: "2026-01-17",
          daysToConvert: 1,
          referredBy: "Direct",
          affiliateId: "AFF1236",
        },
        {
          id: 4,
          publisherName: "David Wilson",
          phone: "0244567890",
          network: "MTN",
          registeredDate: "2026-01-12",
          convertedDate: "2026-01-20",
          daysToConvert: 8,
          referredBy: "INF003",
          affiliateId: "AFF1237",
        },
        {
          id: 5,
          publisherName: "Eva Garcia",
          phone: "0501234567",
          network: "VDF",
          registeredDate: "2026-01-17",
          convertedDate: "2026-01-19",
          daysToConvert: 2,
          referredBy: "INF001",
          affiliateId: "AFF1238",
        },
        {
          id: 6,
          publisherName: "Frank Johnson",
          phone: "0278901234",
          network: "TGO",
          registeredDate: "2026-01-13",
          convertedDate: "2026-01-18",
          daysToConvert: 5,
          referredBy: "INF004",
          affiliateId: "AFF1239",
        },
        {
          id: 7,
          publisherName: "Grace Lee",
          phone: "0245678901",
          network: "MTN",
          registeredDate: "2026-01-18",
          convertedDate: "2026-01-20",
          daysToConvert: 2,
          referredBy: "Direct",
          affiliateId: "AFF1240",
        },
        {
          id: 8,
          publisherName: "Henry Brown",
          phone: "0502345678",
          network: "VDF",
          registeredDate: "2026-01-11",
          convertedDate: "2026-01-15",
          daysToConvert: 4,
          referredBy: "INF005",
          affiliateId: "AFF1241",
        },
      ],
    }),
    []
  );

  // Filtered and sorted conversions
  const filteredConversions = useMemo(() => {
    let filtered = mockConversionData.detailedConversions.filter((conversion) => {
      const matchesSearch =
        conversion.publisherName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        conversion.phone.includes(searchTerm) ||
        conversion.affiliateId.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesNetwork = !networkFilter || conversion.network === networkFilter;
      return matchesSearch && matchesNetwork;
    });

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [mockConversionData.detailedConversions, searchTerm, networkFilter, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  const exportToExcel = () => {
    const dataToExport = filteredConversions.map((conversion, index) => ({
      "#": index + 1,
      "Publisher Name": conversion.publisherName,
      "Phone Number": conversion.phone,
      Network: conversion.network,
      "Registered Date": conversion.registeredDate,
      "Converted Date": conversion.convertedDate,
      "Days to Convert": conversion.daysToConvert,
      "Referred By": conversion.referredBy,
      "Affiliate ID": conversion.affiliateId,
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Conversions");

    const colWidths = [
      { wch: 5 },
      { wch: 25 },
      { wch: 15 },
      { wch: 12 },
      { wch: 18 },
      { wch: 18 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
    ];
    ws["!cols"] = colWidths;

    XLSX.writeFile(wb, `publisher_conversions_${new Date().toISOString().split("T")[0]}.xlsx`);
    toast.success(`Exported ${dataToExport.length} conversion records successfully!`);
  };

  // Chart configurations
  const conversionTrendChartData = {
    labels: mockConversionData.conversionTrend.map((item) =>
      new Date(item.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })
    ),
    datasets: [
      {
        label: "Conversions",
        data: mockConversionData.conversionTrend.map((item) => item.conversions),
        borderColor: "rgb(255, 108, 108)",
        backgroundColor: "rgba(255, 108, 108, 0.1)",
        fill: true,
        tension: 0.4,
      },
      {
        label: "New Potential Publishers",
        data: mockConversionData.conversionTrend.map((item) => item.potentials),
        borderColor: "rgb(147, 197, 253)",
        backgroundColor: "rgba(147, 197, 253, 0.1)",
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const networkBreakdownChartData = {
    labels: Object.keys(mockConversionData.networkBreakdown),
    datasets: [
      {
        label: "Conversion Rate (%)",
        data: Object.values(mockConversionData.networkBreakdown).map((item) => item.rate),
        backgroundColor: ["rgba(255, 193, 7, 0.8)", "rgba(244, 67, 54, 0.8)", "rgba(33, 150, 243, 0.8)"],
        borderColor: ["rgb(255, 193, 7)", "rgb(244, 67, 54)", "rgb(33, 150, 243)"],
        borderWidth: 2,
      },
    ],
  };

  const conversionVelocityChartData = {
    labels: mockConversionData.conversionVelocity.map((item) => item.range),
    datasets: [
      {
        data: mockConversionData.conversionVelocity.map((item) => item.count),
        backgroundColor: [
          "rgba(34, 197, 94, 0.8)",
          "rgba(59, 130, 246, 0.8)",
          "rgba(251, 146, 60, 0.8)",
          "rgba(239, 68, 68, 0.8)",
          "rgba(156, 163, 175, 0.8)",
        ],
        borderColor: ["rgb(34, 197, 94)", "rgb(59, 130, 246)", "rgb(251, 146, 60)", "rgb(239, 68, 68)", "rgb(156, 163, 175)"],
        borderWidth: 2,
      },
    ],
  };

  const dayOfWeekChartData = {
    labels: Object.keys(mockConversionData.conversionsByDayOfWeek),
    datasets: [
      {
        label: "Conversions",
        data: Object.values(mockConversionData.conversionsByDayOfWeek),
        backgroundColor: "rgba(255, 108, 108, 0.8)",
        borderColor: "rgb(255, 108, 108)",
        borderWidth: 2,
        borderRadius: 8,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: "top",
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        padding: 12,
        titleFont: { size: 14, weight: "bold" },
        bodyFont: { size: 13 },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: { display: true, color: "rgba(0, 0, 0, 0.05)" },
      },
      x: {
        grid: { display: false },
      },
    },
  };

  const getNetworkColor = (network) => {
    switch (network) {
      case "MTN":
        return "bg-yellow-100 text-yellow-700 border-yellow-300";
      case "VDF":
        return "bg-red-100 text-red-700 border-red-300";
      case "TGO":
        return "bg-blue-100 text-blue-700 border-blue-300";
      default:
        return "bg-gray-100 text-gray-700 border-gray-300";
    }
  };

  const getNetworkDisplayName = (network) => {
    switch (network) {
      case "VDF":
        return "Telecel";
      case "TGO":
        return "AirtelTigo";
      default:
        return network;
    }
  };

  const statCards = [
    {
      title: "Total Conversions",
      value: mockConversionData.summary.totalConverted.toLocaleString(),
      subtitle: `of ${mockConversionData.summary.totalPotentialPublishers.toLocaleString()} potentials`,
      icon: UserCheck,
      color: "from-green-500 to-emerald-600",
      bgColor: "bg-green-50",
      iconColor: "text-green-600",
    },
    {
      title: "Conversion Rate",
      value: `${mockConversionData.summary.conversionRate}%`,
      subtitle: "Overall success rate",
      icon: Target,
      color: "from-[#FF6C6C] to-[#FF5252]",
      bgColor: "bg-red-50",
      iconColor: "text-[#FF6C6C]",
      trend:
        mockConversionData.summary.growthRate > 0
          ? { value: mockConversionData.summary.growthRate, direction: "up" }
          : null,
    },
    {
      title: "Avg. Conversion Time",
      value: `${mockConversionData.summary.avgConversionTime} days`,
      subtitle: "Time to convert",
      icon: Clock,
      color: "from-blue-500 to-blue-600",
      bgColor: "bg-blue-50",
      iconColor: "text-blue-600",
    },
    {
      title: "This Month",
      value: mockConversionData.summary.thisMonthConversions.toLocaleString(),
      subtitle: `${mockConversionData.summary.growthRate > 0 ? "+" : ""}${mockConversionData.summary.growthRate}% vs last month`,
      icon: TrendingUp,
      color: "from-purple-500 to-purple-600",
      bgColor: "bg-purple-50",
      iconColor: "text-purple-600",
      trend: {
        value: mockConversionData.summary.growthRate,
        direction: mockConversionData.summary.growthRate > 0 ? "up" : "down",
      },
    },
  ];

  return (
    <>
      <Navbar page={"Publisher Conversion Analytics"} />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-red-50/30">
        <div className="p-6">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="flex flex-col gap-4 justify-between items-start mb-6 md:flex-row md:items-center">
              <div>
                <h1 className="flex gap-3 items-center text-3xl font-bold text-gray-900">
                  <div className="p-3 bg-gradient-to-br from-[#FF6C6C] to-[#FF5252] rounded-xl shadow-lg">
                    <Activity className="w-8 h-8 text-white" />
                  </div>
                  Conversion Analytics
                </h1>
                <p className="mt-2 text-gray-600">
                  Track and analyze potential publisher conversions into active affiliates
                </p>
              </div>
              <button
                onClick={exportToExcel}
                className="flex gap-2 items-center justify-center px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-[#FF6C6C] to-[#FF5252] rounded-lg transition-all duration-300 hover:shadow-lg"
              >
                <Download className="w-4 h-4" />
                Export Report
              </button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
              {statCards.map((card, index) => (
                <motion.div
                  key={card.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative overflow-hidden ${card.bgColor} rounded-xl border border-gray-100 shadow-md transition-all duration-300 hover:shadow-lg group`}
                >
                  <div className="relative p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <p className="mb-2 text-sm font-medium text-gray-600">{card.title}</p>
                        <div className="flex gap-2 items-baseline">
                          <p className={`text-3xl font-bold ${card.iconColor}`}>{card.value}</p>
                          {card.trend && (
                            <div
                              className={`flex items-center gap-1 text-sm font-semibold ${
                                card.trend.direction === "up" ? "text-green-600" : "text-red-600"
                              }`}
                            >
                              {card.trend.direction === "up" ? (
                                <ArrowUpRight className="w-4 h-4" />
                              ) : (
                                <ArrowDownRight className="w-4 h-4" />
                              )}
                              {Math.abs(card.trend.value)}%
                            </div>
                          )}
                        </div>
                        <p className="mt-2 text-xs text-gray-500">{card.subtitle}</p>
                      </div>
                      <div
                        className={`p-3 bg-white rounded-lg shadow-sm group-hover:scale-110 transition-transform ${card.iconColor}`}
                      >
                        <card.icon className="w-6 h-6" />
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 gap-6 mb-6 lg:grid-cols-2">
            {/* Conversion Trend */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="p-6 bg-white rounded-xl border border-gray-100 shadow-md"
            >
              <div className="flex justify-between items-center mb-4">
                <div className="flex gap-2 items-center">
                  <BarChart3 className="w-5 h-5 text-[#FF6C6C]" />
                  <h3 className="text-lg font-semibold text-gray-900">Conversion Trend</h3>
                </div>
                <select
                  value={timeRange}
                  onChange={(e) => setTimeRange(e.target.value)}
                  className="px-3 py-2 text-sm text-gray-700 bg-white rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C]"
                >
                  <option value="7days">Last 7 Days</option>
                  <option value="30days">Last 30 Days</option>
                  <option value="90days">Last 90 Days</option>
                  <option value="1year">Last Year</option>
                </select>
              </div>
              <div className="h-80">
                <Line data={conversionTrendChartData} options={chartOptions} />
              </div>
            </motion.div>

            {/* Network Breakdown */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="p-6 bg-white rounded-xl border border-gray-100 shadow-md"
            >
              <div className="flex gap-2 items-center mb-4">
                <Network className="w-5 h-5 text-[#FF6C6C]" />
                <h3 className="text-lg font-semibold text-gray-900">Network Performance</h3>
              </div>
              <div className="h-80">
                <Bar data={networkBreakdownChartData} options={chartOptions} />
              </div>
              <div className="grid grid-cols-3 gap-4 mt-4">
                {Object.entries(mockConversionData.networkBreakdown).map(([network, data]) => (
                  <div key={network} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-xs font-medium text-gray-600">{getNetworkDisplayName(network)}</p>
                    <p className="mt-1 text-xl font-bold text-gray-900">{data.converted}</p>
                    <p className="text-xs text-gray-500">of {data.total} ({data.rate}%)</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* More Charts */}
          <div className="grid grid-cols-1 gap-6 mb-6 lg:grid-cols-3">
            {/* Conversion Velocity */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="p-6 bg-white rounded-xl border border-gray-100 shadow-md"
            >
              <div className="flex gap-2 items-center mb-4">
                <Zap className="w-5 h-5 text-[#FF6C6C]" />
                <h3 className="text-lg font-semibold text-gray-900">Conversion Speed</h3>
              </div>
              <div className="h-64">
                <Doughnut
                  data={conversionVelocityChartData}
                  options={{
                    ...chartOptions,
                    plugins: {
                      ...chartOptions.plugins,
                      legend: { display: false },
                    },
                    cutout: "60%",
                  }}
                />
              </div>
              <div className="mt-4 space-y-2">
                {mockConversionData.conversionVelocity.map((item, index) => (
                  <div key={index} className="flex justify-between items-center text-sm">
                    <div className="flex gap-2 items-center">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{
                          backgroundColor: conversionVelocityChartData.datasets[0].backgroundColor[index],
                        }}
                      />
                      <span className="text-gray-700">{item.range}</span>
                    </div>
                    <div className="flex gap-2 items-center">
                      <span className="font-semibold text-gray-900">{item.count}</span>
                      <span className="text-gray-500">({item.percentage}%)</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Day of Week Analysis */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="p-6 bg-white rounded-xl border border-gray-100 shadow-md"
            >
              <div className="flex gap-2 items-center mb-4">
                <Calendar className="w-5 h-5 text-[#FF6C6C]" />
                <h3 className="text-lg font-semibold text-gray-900">Conversions by Day</h3>
              </div>
              <div className="h-64">
                <Bar
                  data={dayOfWeekChartData}
                  options={{
                    ...chartOptions,
                    plugins: {
                      ...chartOptions.plugins,
                      legend: { display: false },
                    },
                  }}
                />
              </div>
              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs font-medium text-blue-900">Peak Conversion Days</p>
                <p className="mt-1 text-sm text-blue-800">
                  Tuesday & Wednesday show the highest conversion rates
                </p>
              </div>
            </motion.div>

            {/* Top Referrers */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="p-6 bg-white rounded-xl border border-gray-100 shadow-md"
            >
              <div className="flex gap-2 items-center mb-4">
                <Award className="w-5 h-5 text-[#FF6C6C]" />
                <h3 className="text-lg font-semibold text-gray-900">Top Referrers</h3>
              </div>
              <div className="space-y-3">
                {mockConversionData.topReferrers.map((referrer, index) => (
                  <div
                    key={referrer.code}
                    className="flex gap-3 items-center p-3 bg-gray-50 rounded-lg border border-gray-200 transition-all hover:shadow-md"
                  >
                    <div className="flex justify-center items-center w-10 h-10 bg-gradient-to-br from-[#FF6C6C] to-[#FF5252] rounded-full shadow-md">
                      <span className="text-sm font-bold text-white">#{index + 1}</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-900">{referrer.name}</p>
                      <p className="text-xs text-gray-500">{referrer.code}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-[#FF6C6C]">{referrer.conversions}</p>
                      <p className="text-xs text-gray-500">{referrer.rate}% rate</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 bg-gradient-to-r from-red-50 to-orange-50 rounded-lg border border-red-200">
                <p className="text-xs font-medium text-red-900">Referral Impact</p>
                <p className="mt-1 text-sm text-red-800">
                  Referred publishers convert 42% faster on average
                </p>
              </div>
            </motion.div>
          </div>

          {/* Detailed Conversions Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="overflow-hidden bg-white rounded-xl border border-gray-100 shadow-md"
          >
            <div className="p-6 border-b border-gray-100">
              <div className="flex flex-col gap-4 justify-between items-start md:flex-row md:items-center">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Conversion Details</h3>
                  <p className="mt-1 text-sm text-gray-600">
                    Complete list of successful conversions
                  </p>
                </div>
                <div className="flex flex-col gap-3 w-full sm:flex-row md:w-auto">
                  <div className="relative w-full sm:w-64">
                    <input
                      type="text"
                      placeholder="Search conversions..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] transition-all duration-200 outline-none"
                    />
                    <Search
                      size={18}
                      className="absolute left-3 top-1/2 text-gray-400 transform -translate-y-1/2"
                    />
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm("")}
                        className="absolute right-3 top-1/2 text-gray-400 transform -translate-y-1/2 hover:text-gray-600"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                  <select
                    value={networkFilter}
                    onChange={(e) => setNetworkFilter(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] transition-all duration-200 outline-none"
                  >
                    <option value="">All Networks</option>
                    <option value="MTN">MTN</option>
                    <option value="VDF">Telecel</option>
                    <option value="TGO">AirtelTigo</option>
                  </select>
                </div>
              </div>
              {(searchTerm || networkFilter) && (
                <div className="flex gap-2 items-center mt-4">
                  <span className="text-sm text-gray-600">
                    Showing {filteredConversions.length} of {mockConversionData.detailedConversions.length} conversions
                  </span>
                  <button
                    onClick={() => {
                      setSearchTerm("");
                      setNetworkFilter("");
                    }}
                    className="text-sm font-medium text-[#FF6C6C] hover:underline"
                  >
                    Clear filters
                  </button>
                </div>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-xs font-semibold tracking-wider text-left text-gray-600 uppercase">
                      #
                    </th>
                    <th
                      onClick={() => handleSort("publisherName")}
                      className="px-6 py-4 text-xs font-semibold tracking-wider text-left text-gray-600 uppercase cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-1">
                        Publisher
                        {sortConfig.key === "publisherName" ? (
                          sortConfig.direction === "asc" ? (
                            <ChevronUp size={14} />
                          ) : (
                            <ChevronDown size={14} />
                          )
                        ) : (
                          <ChevronDown size={14} className="opacity-30" />
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-4 text-xs font-semibold tracking-wider text-left text-gray-600 uppercase">
                      Contact
                    </th>
                    <th className="px-6 py-4 text-xs font-semibold tracking-wider text-left text-gray-600 uppercase">
                      Network
                    </th>
                    <th
                      onClick={() => handleSort("registeredDate")}
                      className="px-6 py-4 text-xs font-semibold tracking-wider text-left text-gray-600 uppercase cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-1">
                        Registered
                        {sortConfig.key === "registeredDate" ? (
                          sortConfig.direction === "asc" ? (
                            <ChevronUp size={14} />
                          ) : (
                            <ChevronDown size={14} />
                          )
                        ) : (
                          <ChevronDown size={14} className="opacity-30" />
                        )}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort("convertedDate")}
                      className="px-6 py-4 text-xs font-semibold tracking-wider text-left text-gray-600 uppercase cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-1">
                        Converted
                        {sortConfig.key === "convertedDate" ? (
                          sortConfig.direction === "asc" ? (
                            <ChevronUp size={14} />
                          ) : (
                            <ChevronDown size={14} />
                          )
                        ) : (
                          <ChevronDown size={14} className="opacity-30" />
                        )}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort("daysToConvert")}
                      className="px-6 py-4 text-xs font-semibold tracking-wider text-left text-gray-600 uppercase cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-1">
                        Speed
                        {sortConfig.key === "daysToConvert" ? (
                          sortConfig.direction === "asc" ? (
                            <ChevronUp size={14} />
                          ) : (
                            <ChevronDown size={14} />
                          )
                        ) : (
                          <ChevronDown size={14} className="opacity-30" />
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-4 text-xs font-semibold tracking-wider text-left text-gray-600 uppercase">
                      Referred By
                    </th>
                    <th className="px-6 py-4 text-xs font-semibold tracking-wider text-left text-gray-600 uppercase">
                      Affiliate ID
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredConversions.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="px-6 py-12 text-center">
                        <div className="flex flex-col justify-center items-center">
                          <Users className="mb-3 w-16 h-16 text-gray-300" />
                          <p className="font-medium text-gray-500">No conversions found</p>
                          <p className="mt-1 text-sm text-gray-400">
                            Try adjusting your filters
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredConversions.map((conversion, index) => (
                      <motion.tr
                        key={conversion.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: index * 0.02 }}
                        className="transition-colors duration-150 hover:bg-red-50/30"
                      >
                        <td className="px-6 py-4">
                          <span className="text-sm font-medium text-gray-600">{index + 1}</span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-3 items-center">
                            <div className="flex justify-center items-center w-10 h-10 bg-gradient-to-br from-[#FF6C6C] to-[#FF5252] rounded-full shadow-md">
                              <span className="text-sm font-semibold text-white">
                                {conversion.publisherName.charAt(0)}
                              </span>
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">
                                {conversion.publisherName}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex gap-2 items-center">
                            <Phone className="w-4 h-4 text-gray-400" />
                            <span className="font-mono text-sm text-gray-900">
                              {conversion.phone}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${getNetworkColor(
                              conversion.network
                            )}`}
                          >
                            {getNetworkDisplayName(conversion.network)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-600">
                            {new Date(conversion.registeredDate).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-green-600">
                            {new Date(conversion.convertedDate).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex gap-1 items-center">
                            <Clock className="w-4 h-4 text-gray-400" />
                            <span
                              className={`text-sm font-semibold ${
                                conversion.daysToConvert <= 3
                                  ? "text-green-600"
                                  : conversion.daysToConvert <= 7
                                  ? "text-yellow-600"
                                  : "text-orange-600"
                              }`}
                            >
                              {conversion.daysToConvert}d
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {conversion.referredBy !== "Direct" ? (
                            <span className="inline-flex items-center px-3 py-1 text-xs font-semibold text-[#FF6C6C] bg-gradient-to-r from-red-50 to-orange-50 rounded-lg border border-red-200">
                              {conversion.referredBy}
                            </span>
                          ) : (
                            <span className="text-sm italic text-gray-400">Direct</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="font-mono text-sm font-semibold text-gray-900">
                            {conversion.affiliateId}
                          </span>
                        </td>
                      </motion.tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {filteredConversions.length > 0 && (
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  Showing {filteredConversions.length} of{" "}
                  {mockConversionData.detailedConversions.length} total conversions
                </p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default PublisherConversionAnalytics;
